import { memo, useMemo, useRef } from 'react';
import type { NoteEvent, Measure } from '../../music/piece';
import { fifthsPosition, parseKey } from '../../music/key';
import type { PieceView } from '../model';

/**
 * The music under the playhead, as a moving score: a grand staff in proportional notation.
 * Noteheads sit on the staff by their spelled pitch and along it by time (space is time, not
 * engraving), with stems, flags and dots for the written lengths, ledger lines, accidentals
 * against the key signature, and barlines with bar numbers. Clef and key signature stay put at
 * the left; the music moves under a fixed playhead. A band under the staff shows the tension of
 * each chord on the same time axis. Drag the strip to scrub, tap it to jump.
 */

interface Props {
  notes: NoteEvent[];
  measures: Measure[];
  view: PieceView;
  homeKey: string;
  position: number;
  width: number;
  /** Staff space in px (the distance between two staff lines). */
  space: number;
  /** In the fog, only what has been heard is shown in the tension band. */
  fog: boolean;
  /** Chorales: S and A on the treble staff, T and B on the bass, stems by voice. */
  satb: boolean;
  /** Height of the tension band under the staff (0 hides it). */
  band: number;
  label: string;
  onSeek: (t: number) => void;
}

const LETTER: Record<string, number> = { C: 0, D: 1, E: 2, F: 3, G: 4, A: 5, B: 6 };
const SHARP_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

interface Spelled {
  step: number; // diatonic step: octave × 7 + letter (C4 = 28)
  letter: number;
  alter: number;
  octave: number;
}

function spell(n: NoteEvent): Spelled {
  const name = n.name || `${SHARP_NAMES[((n.midi % 12) + 12) % 12]}${Math.floor(n.midi / 12) - 1}`;
  const m = /^([A-G])(#{1,2}|b{1,2}|-{1,2})?(-?\d+)$/.exec(name);
  if (!m) {
    const fallback = `${SHARP_NAMES[((n.midi % 12) + 12) % 12]}${Math.floor(n.midi / 12) - 1}`;
    return spell({ ...n, name: fallback });
  }
  const letter = LETTER[m[1]];
  const acc = m[2] ?? '';
  const alter = acc.startsWith('#') ? acc.length : acc ? -acc.length : 0;
  const octave = Number(m[3]);
  return { step: octave * 7 + letter, letter, alter, octave };
}

// Steps of the staff lines. Treble: E4 … F5; bass: G2 … A3.
const TREBLE_BOTTOM = 30;
const TREBLE_TOP = 38;
const BASS_BOTTOM = 18;
const BASS_TOP = 26;
const SHARP_ORDER = [3, 0, 4, 1, 5, 2, 6]; // F C G D A E B
const FLAT_ORDER = [6, 2, 5, 1, 4, 0, 3]; // B E A D G C F
const SHARP_STEPS = [38, 35, 39, 36, 33, 37, 34]; // treble positions; bass is two octaves lower
const FLAT_STEPS = [34, 37, 33, 36, 32, 35, 31];

/* Glyphs, drawn in staff spaces (1 = one space), origin at the glyph's reference point. */
const TREBLE_CLEF =
  'M1.32 3.08C0.92 3.1 0.78 2.52 1.28 2.38C1.92 2.24 2.24 3.02 1.92 3.62C1.6 4.24 0.46 4.16 0.26 3.3C0.06 2.38 0.92 1.58 1.5 0.92C2.02 0.3 2.12 -0.92 1.7 -1.32C1.3 -1.62 0.88 -0.62 0.98 0.32L1.52 5.02C1.6 5.62 1.02 6.02 0.6 5.62';
const BASS_CLEF = 'M0.22 1.02C0.18 0.2 1.02 -0.18 1.56 0.2C2.18 0.62 2.06 1.66 1.52 2.34C1.02 2.98 0.52 3.38 0.06 3.7';

function Accidental({ alter, x, y, s }: { alter: number; x: number; y: number; s: number }) {
  const t = (d: number) => d * s;
  if (alter === 1)
    return (
      <g transform={`translate(${x},${y})`} className="acc">
        <path d={`M${t(-0.22)},${t(-1.15)}V${t(1.25)}M${t(0.22)},${t(-1.25)}V${t(1.15)}`} strokeWidth={Math.max(1, t(0.1))} />
        <path d={`M${t(-0.48)},${t(-0.22)}L${t(0.48)},${t(-0.5)}M${t(-0.48)},${t(0.5)}L${t(0.48)},${t(0.22)}`} strokeWidth={Math.max(1.4, t(0.24))} />
      </g>
    );
  if (alter === -1)
    return (
      <g transform={`translate(${x},${y})`} className="acc">
        <path d={`M${t(-0.32)},${t(-1.7)}V${t(0.5)}C${t(0.42)},${t(0.08)} ${t(0.56)},${t(-0.56)} ${t(-0.32)},${t(-0.2)}`} strokeWidth={Math.max(1.1, t(0.16))} fill="none" />
      </g>
    );
  if (alter === 0)
    return (
      <g transform={`translate(${x},${y})`} className="acc">
        <path d={`M${t(-0.25)},${t(-1.3)}V${t(0.5)}M${t(0.25)},${t(-0.5)}V${t(1.3)}`} strokeWidth={Math.max(1, t(0.1))} />
        <path d={`M${t(-0.25)},${t(0.4)}L${t(0.25)},${t(0.22)}M${t(-0.25)},${t(-0.22)}L${t(0.25)},${t(-0.4)}`} strokeWidth={Math.max(1.4, t(0.22))} />
      </g>
    );
  if (alter === 2)
    return (
      <g transform={`translate(${x},${y})`} className="acc">
        <path d={`M${t(-0.35)},${t(-0.35)}L${t(0.35)},${t(0.35)}M${t(-0.35)},${t(0.35)}L${t(0.35)},${t(-0.35)}`} strokeWidth={Math.max(1.4, t(0.2))} />
      </g>
    );
  return (
    <g className="acc">
      <Accidental alter={-1} x={x - t(0.55)} y={y} s={s} />
      <Accidental alter={-1} x={x + t(0.1)} y={y} s={s} />
    </g>
  );
}

interface Placed {
  n: NoteEvent;
  p: Spelled;
  staff: 0 | 1; // 0 treble, 1 bass
  y: number;
  /** Stem direction: -1 up, 1 down. */
  dir: number;
  acc: number | null;
}

function ScoreStripInner({ notes, measures, view, homeKey, position, width, space: g, fog, satb, band, label, onSeek }: Props) {
  const key = parseKey(homeKey);
  const fifths = Math.max(-7, Math.min(7, fifthsPosition(key)));
  // Vertical layout, in px.
  const top = 4.2 * g;
  const trebleTopY = top;
  const trebleBottomY = trebleTopY + 4 * g;
  const bassTopY = trebleBottomY + 4.2 * g;
  const bassBottomY = bassTopY + 4 * g;
  const staffH = bassBottomY + 3.8 * g;
  const H = staffH + band;
  const yOf = (step: number, staff: 0 | 1) => (staff === 0 ? trebleBottomY - ((step - TREBLE_BOTTOM) * g) / 2 : bassBottomY - ((step - BASS_BOTTOM) * g) / 2);

  // Horizontal layout: a fixed panel (clef, key signature), then the music under a fixed playhead.
  const panelW = Math.round(g * (3.4 + Math.abs(fifths) * 1.05) + 8);
  const playX = panelW + Math.max(40, (width - panelW) * 0.3);

  // Space per quarter: the usual distance between onsets gets about four staff spaces.
  const pxPerQ = useMemo(() => {
    const onsets = [...new Set(notes.map((n) => Math.round(n.onset * 1000) / 1000))].sort((a, b) => a - b);
    const gaps: number[] = [];
    for (let i = 1; i < onsets.length; i++) if (onsets[i] - onsets[i - 1] > 1e-3) gaps.push(onsets[i] - onsets[i - 1]);
    gaps.sort((a, b) => a - b);
    const typical = gaps.length ? gaps[Math.floor(gaps.length * 0.3)] : 1;
    return Math.max(5 * g, Math.min(30 * g, (4.2 * g) / typical));
  }, [notes, g]);

  // Everything that depends only on the notes: staff, height, stems, accidentals in each bar.
  const placed = useMemo<Placed[]>(() => {
    const keyAlter = new Array(7).fill(0);
    if (fifths > 0) for (let i = 0; i < fifths; i++) keyAlter[SHARP_ORDER[i]] = 1;
    if (fifths < 0) for (let i = 0; i < -fifths; i++) keyAlter[FLAT_ORDER[i]] = -1;
    const sorted = [...notes].sort((a, b) => a.onset - b.onset || a.midi - b.midi);
    const memory = new Map<string, number>();
    let bar = Number.NaN;
    const out: Placed[] = [];
    for (const n of sorted) {
      const p = spell(n);
      // Staff: the voices of a chorale as Bach wrote them; the hands of a keyboard score where the
      // source says; otherwise by pitch, keeping the lowest line (an Alberti bass around middle C)
      // in the bass staff up to A4.
      const staff: 0 | 1 =
        satb && n.line !== undefined
          ? n.line >= 2
            ? 0
            : 1
          : n.staff !== undefined
            ? n.staff === 1
              ? 0
              : 1
            : n.line === 0 && n.midi < 69
              ? 1
              : n.midi >= 60
                ? 0
                : 1;
      if (n.bar !== bar) {
        memory.clear();
        bar = n.bar;
      }
      const slot = `${staff}:${p.step}`;
      const expected = memory.get(slot) ?? keyAlter[p.letter];
      const acc = p.alter !== expected ? p.alter : null;
      memory.set(slot, p.alter);
      const middle = staff === 0 ? 34 : 22;
      const dir = satb && n.line !== undefined ? (n.line % 2 === 1 ? -1 : 1) : p.step < middle ? -1 : 1;
      out.push({ n, p, staff, y: yOf(p.step, staff), dir, acc });
    }
    return out;
    // yOf depends only on g.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, satb, fifths, g]);

  const x = (t: number) => playX + (t - position) * pxPerQ;
  const t0 = position - (playX - panelW) / pxPerQ;
  const t1 = position + (width - playX) / pxPerQ;

  // Notes in view (and long notes that started before it).
  const visible = placed.filter((q) => q.n.onset <= t1 && q.n.onset + q.n.dur >= t0 - 0.5);
  const rx = 0.64 * g;
  const ry = 0.45 * g;

  // Stems: one per onset, staff and direction, through every head of the chord.
  const groups = new Map<string, Placed[]>();
  for (const q of visible) {
    if (q.n.dur >= 4 - 1e-6) continue;
    const k = `${q.n.onset.toFixed(4)}|${q.staff}|${q.dir}`;
    const list = groups.get(k) ?? [];
    list.push(q);
    groups.set(k, list);
  }

  const lines = (y0: number) => [0, 1, 2, 3, 4].map((i) => y0 + i * g);
  const staffLines = [...lines(trebleTopY), ...lines(bassTopY)];
  const inBars = measures.filter((m) => m.start >= t0 - 1e-6 && m.start <= t1 + 1e-6);

  // Tension band: each chord as a block, coloured from rest to tension.
  const bandY = staffH;
  const bandEvents = band > 0 ? view.events.filter((ev) => ev.end >= t0 && ev.start <= (fog ? Math.min(t1, position) : t1)) : [];

  // Scrub by dragging, jump by tapping.
  const drag = useRef<{ x: number; pos: number; moved: boolean; id: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const localX = (e: React.PointerEvent) => e.clientX - (svgRef.current?.getBoundingClientRect().left ?? 0);
  const total = measures.length ? measures[measures.length - 1].start + measures[measures.length - 1].length : 0;
  const clamp = (t: number) => Math.max(0, Math.min(total - 1e-3, t));

  const clefStroke = Math.max(1.3, 0.2 * g);

  return (
    <svg
      ref={svgRef}
      className="strip-svg"
      width={width}
      height={H}
      viewBox={`0 0 ${width} ${H}`}
      role="img"
      aria-label={label}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        drag.current = { x: localX(e), pos: position, moved: false, id: e.pointerId };
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d || d.id !== e.pointerId) return;
        const dx = localX(e) - d.x;
        if (!d.moved && Math.abs(dx) < 6) return;
        if (!d.moved) {
          d.moved = true;
          (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
        }
        onSeek(clamp(d.pos - dx / pxPerQ));
      }}
      onPointerUp={(e) => {
        const d = drag.current;
        drag.current = null;
        if (!d || d.moved) return;
        const lx = localX(e);
        if (lx < panelW) return;
        onSeek(clamp(position + (lx - playX) / pxPerQ));
      }}
      onPointerCancel={() => (drag.current = null)}
    >
      <defs>
        <clipPath id="strip-music">
          <rect x={panelW} y={0} width={Math.max(0, width - panelW)} height={H} />
        </clipPath>
      </defs>

      {/* Staff lines run under the panel and the music alike. */}
      {staffLines.map((y, i) => (
        <line key={i} x1={4} x2={width} y1={y} y2={y} className="strip-line" />
      ))}
      <line x1={4} x2={4} y1={trebleTopY} y2={bassBottomY} className="strip-line" />

      <g clipPath="url(#strip-music)">
        {/* Tension band */}
        {bandEvents.map((ev) => {
          const v = view.tension.perEvent[ev.index]?.value ?? 0;
          const a = Math.max(x(ev.start), panelW);
          const b = Math.min(x(fog ? Math.min(ev.end, position) : ev.end), width);
          if (b <= a) return null;
          return (
            <rect
              key={ev.index}
              x={a}
              y={bandY + 3}
              width={Math.max(0, b - a - 1)}
              height={Math.max(2, band - 6)}
              rx={2}
              style={{ fill: `color-mix(in oklab, var(--tension) ${Math.round(Math.min(1, v) * 100)}%, var(--rest))`, opacity: 0.35 + 0.55 * Math.min(1, v) }}
            />
          );
        })}

        {/* Barlines and bar numbers */}
        {inBars.map((m) => (
          <g key={m.bar}>
            <line x1={x(m.start)} x2={x(m.start)} y1={trebleTopY} y2={bassBottomY} className="strip-bar" />
            {m.bar > 0 && (
              <text x={x(m.start) + 3} y={trebleTopY - 1.6 * g} className="strip-num">
                {m.bar}
              </text>
            )}
          </g>
        ))}

        {/* Length of each note, faint: in proportional notation space is time. */}
        {visible.map((q, i) => {
          const a = x(q.n.onset) + rx * 2;
          const b = x(q.n.onset + q.n.dur) - 2;
          return b - a > 2 ? <rect key={`d${i}`} x={a} y={q.y - 0.16 * g} width={b - a} height={0.32 * g} rx={0.16 * g} className="strip-dur" data-on={q.n.onset <= position && position < q.n.onset + q.n.dur} /> : null;
        })}

        {/* Ledger lines */}
        {visible.map((q, i) => {
          const s = q.p.step;
          const ys: number[] = [];
          if (q.staff === 0) {
            for (let st = TREBLE_TOP + 2; st <= s; st += 2) ys.push(yOf(st, 0));
            for (let st = TREBLE_BOTTOM - 2; st >= s; st -= 2) ys.push(yOf(st, 0));
          } else {
            for (let st = BASS_TOP + 2; st <= s; st += 2) ys.push(yOf(st, 1));
            for (let st = BASS_BOTTOM - 2; st >= s; st -= 2) ys.push(yOf(st, 1));
          }
          const cx = x(q.n.onset) + rx;
          return ys.map((y, j) => <line key={`l${i}-${j}`} x1={cx - rx * 1.6} x2={cx + rx * 1.6} y1={y} y2={y} className="strip-ledger" />);
        })}

        {/* Stems and flags */}
        {[...groups.values()].map((list, i) => {
          const q0 = list[0];
          const ys = list.map((q) => q.y);
          const dir = q0.dir;
          const cx = x(q0.n.onset) + rx;
          const sx = dir < 0 ? cx + rx * 0.92 : cx - rx * 0.92;
          const from = dir < 0 ? Math.max(...ys) : Math.min(...ys);
          const to = dir < 0 ? Math.min(...ys) - 3.3 * g : Math.max(...ys) + 3.3 * g;
          const dur = Math.min(...list.map((q) => q.n.dur));
          const flags = dur < 1 - 1e-6 ? Math.max(1, Math.floor(-Math.log2(dur) + 0.4)) : 0;
          return (
            <g key={`s${i}`} className="strip-stem" data-past={q0.n.onset + q0.n.dur <= position}>
              <line x1={sx} x2={sx} y1={from} y2={to} strokeWidth={Math.max(1, 0.12 * g)} />
              {Array.from({ length: Math.min(4, flags) }, (_, k) => {
                const fy = to - dir * k * 0.75 * g;
                return <path key={k} d={`M${sx},${fy} q${1.25 * g},${-dir * 0.8 * g} ${1.05 * g},${-dir * 2.5 * g}`} fill="none" strokeWidth={Math.max(1.2, 0.22 * g)} />;
              })}
            </g>
          );
        })}

        {/* Noteheads, accidentals, dots */}
        {visible.map((q, i) => {
          const cx = x(q.n.onset) + rx;
          const hollow = q.n.dur >= 2 - 1e-6;
          const on = q.n.onset <= position + 1e-6 && position < q.n.onset + q.n.dur - 1e-6;
          const past = q.n.onset + q.n.dur <= position + 1e-6;
          const dotted = [0.375, 0.75, 1.5, 3, 6].some((d) => Math.abs(q.n.dur - d) < 1e-3);
          const onLine = (q.staff === 0 ? q.p.step - TREBLE_BOTTOM : q.p.step - BASS_BOTTOM) % 2 === 0;
          return (
            <g key={`h${i}`} className="strip-note" data-on={on} data-past={past} data-altered={!!q.n.altered}>
              {q.acc !== null && <Accidental alter={q.acc} x={cx - rx - 0.75 * g} y={q.y} s={g} />}
              <ellipse cx={cx} cy={q.y} rx={rx} ry={ry} transform={`rotate(-20 ${cx} ${q.y})`} className={hollow ? 'head hollow' : 'head'} strokeWidth={hollow ? Math.max(1.1, 0.2 * g) : 0} />
              {dotted && <circle cx={cx + rx + 0.55 * g} cy={onLine ? q.y - g / 2 : q.y} r={Math.max(1, 0.17 * g)} className="head" />}
            </g>
          );
        })}
      </g>

      {/* The fixed panel: clefs and key signature, over the music that passes under it. */}
      <rect x={0} y={0} width={panelW} height={staffH} className="strip-panel" />
      {staffLines.map((y, i) => (
        <line key={`p${i}`} x1={4} x2={panelW} y1={y} y2={y} className="strip-line" />
      ))}
      <line x1={4} x2={4} y1={trebleTopY} y2={bassBottomY} className="strip-line strong" />
      <g transform={`translate(${g * 0.5},${trebleTopY}) scale(${g})`} className="strip-clef">
        <path d={TREBLE_CLEF} strokeWidth={clefStroke / g} fill="none" />
        <circle cx={0.78} cy={5.5} r={0.3} className="strip-clef-dot" />
      </g>
      <g transform={`translate(${g * 0.55},${bassTopY}) scale(${g})`} className="strip-clef">
        <path d={BASS_CLEF} strokeWidth={(clefStroke * 1.15) / g} fill="none" />
        <circle cx={0.36} cy={1.02} r={0.34} className="strip-clef-dot" />
        <circle cx={2.4} cy={0.52} r={0.17} className="strip-clef-dot" />
        <circle cx={2.4} cy={1.52} r={0.17} className="strip-clef-dot" />
      </g>
      {Array.from({ length: Math.abs(fifths) }, (_, i) => {
        const steps = fifths > 0 ? SHARP_STEPS : FLAT_STEPS;
        const ax = g * (3.6 + i * 1.0);
        return (
          <g key={i}>
            <Accidental alter={fifths > 0 ? 1 : -1} x={ax} y={yOf(steps[i], 0)} s={g} />
            <Accidental alter={fifths > 0 ? 1 : -1} x={ax} y={yOf(steps[i] - 14, 1)} s={g} />
          </g>
        );
      })}
      <line x1={panelW} x2={panelW} y1={trebleTopY} y2={bassBottomY} className="strip-edge" />

      {/* Playhead */}
      <line x1={playX} x2={playX} y1={2} y2={H - 2} className="strip-playhead" />
      <path d={`M${playX - 5},1 h10 l-5,6 z`} className="strip-playhead-cap" />
    </svg>
  );
}

export const ScoreStrip = memo(ScoreStripInner);
